import { createSaveSearchKeyBySearchParams } from './saveSearch';
import { createSelector } from '@reduxjs/toolkit';
import { getAuthToken } from './user';
import { getDeployment } from './config';
import { getItemSavedSearchQuery } from '@/utils/ItemPageUtils';
import { GlobalState } from '../rootReducer';
import { isUserLoggedIn } from '@/redux/modules/user';
import { SaveSearchCheckState } from '@/types/SaveSearch';
import api from '@/redux/api/saveSearch';
import cloneDeep from 'lodash/cloneDeep';
import difference from 'lodash/difference';
import ms from 'ms';
import union from 'lodash/union';

const REDUX_STORE_TIME = ms('1m');

/* Action Types */
export const SAVE_SEARCH_CHECK_FAIL = 'la/ui/saveSearchCheck/FAIL';
export const SAVE_SEARCH_CHECK_REQUEST = 'la/ui/saveSearchCheck/REQUEST';
export const SAVE_SEARCH_CHECK_SUCCESS = 'la/ui/saveSearchCheck/SUCCESS';

// reducer
interface DefaultState {
    byId: {
        [key: string]: SaveSearchCheckState;
    };
    error?: boolean;
    loaded: { [id: number]: number };
    // array of saved Search keys that are in the middle of being loaded
    loading: string[];
}

export const DEFAULT_STATE: DefaultState = {
    byId: {},
    error: false,
    loaded: {},
    loading: [],
};

export type State = typeof DEFAULT_STATE;

export default function reducer(state: State = DEFAULT_STATE, action: any = {}) {
    let existing;
    let loading: typeof state.loading;
    let loaded: typeof state.loaded;

    switch (action.type) {
        case SAVE_SEARCH_CHECK_FAIL:
            return {
                ...state,
                error: true,
                loading: difference(state.loading, action.payload.saveSearchKey),
            };
        case SAVE_SEARCH_CHECK_REQUEST:
            return {
                ...state,
                error: false,
                loading: union(state.loading, action.payload.saveSearchKey),
            };
        case SAVE_SEARCH_CHECK_SUCCESS:
            existing = cloneDeep(state.byId);
            loading = [...state.loading];
            loading = difference(loading, [action.payload.saveSearchKey]);
            loaded = { ...state.loaded };

            if (action.payload.saveSearchKey) {
                existing[action.payload.saveSearchKey] = {
                    isSavedSearch: action.payload.savedSearchId > 0,
                    key: action.payload.saveSearchKey,
                    savedSearchId: action.payload.savedSearchId,
                    success: true,
                };
                loaded[action.payload.saveSearchKey] = action.meta.actionTime;
            }

            return {
                ...state,
                byId: existing,
                error: false,
                loaded,
                loading,
            };
        default:
            return state;
    }
}

/* SELECTORS */
const stateSelector = (state: GlobalState) => state.saveSearchCheck;

export const getSaveSearchCheckState = createSelector(stateSelector, (state) => state);

const idSelector = (state: GlobalState, id: string) => id;

const loadedSelector = createSelector(stateSelector, (state) => state.loaded);

const loadingSelector = createSelector(stateSelector, (state) => state.loading);

const getLoadTimeForSaveSearchCheck = createSelector([loadedSelector, idSelector], (loaded, id) => loaded[id] || 0);

const isSaveSearchCheckLoading = createSelector([loadingSelector, idSelector], (loading, id) => loading.includes(id));

export const getSaveSearchCheckStateByKey = createSelector(
    [stateSelector, idSelector],
    (state, key) =>
        state.byId[key] || {
            isSavedSearch: false,
            key,
            savedSearchId: 0,
            success: true,
        }
);

const shouldSubmitSavedSearchCheck = createSelector(
    [idSelector, getSaveSearchCheckStateByKey, getLoadTimeForSaveSearchCheck, isSaveSearchCheckLoading],
    (key, savedSearchCheck, loaded, loading) => {
        if (!key) {
            return false;
        }

        if (savedSearchCheck?.key || Boolean(loaded)) {
            const time = Date.now();
            const diff = time - loaded;
            if (diff < REDUX_STORE_TIME) {
                return false;
            }
        }
        return !loading;
    }
);

export const getSavedSearchIdByKey = createSelector([getSaveSearchCheckStateByKey], (stateForKey) => {
    return stateForKey?.savedSearchId || 0;
});

/* ACTION CREATORS */
export const submitIsSavedSearchCheck = (ssParams: any) => async (dispatch: Function, getState: Function) => {
    try {
        const saveSearchKey = createSaveSearchKeyBySearchParams(ssParams);
        const state = getState();
        const userLoggedIn = isUserLoggedIn(state);

        if (!shouldSubmitSavedSearchCheck(state, saveSearchKey) || !userLoggedIn) {
            return Promise.resolve();
        }

        const authToken = getAuthToken(state);
        const deployment = getDeployment(state);
        dispatch({
            payload: { saveSearchKey },
            type: SAVE_SEARCH_CHECK_REQUEST,
        });

        const response = await api.postIsSavedSearch({ authToken, deployment, ssParams });
        dispatch({
            meta: { actionTime: Date.now() },
            payload: { savedSearchId: response.payload, saveSearchKey },
            type: SAVE_SEARCH_CHECK_SUCCESS,
        });
    } catch (error) {
        const saveSearchKey = createSaveSearchKeyBySearchParams(ssParams);
        dispatch({
            payload: { saveSearchKey },
            type: SAVE_SEARCH_CHECK_FAIL,
        });
    }
};

export const submitItemPageSavedSearchCheck =
    (itemPageFacets: any) => async (dispatch: Function, getState: Function) => {
        const ssParams = getItemSavedSearchQuery(itemPageFacets);
        const isLoggedIn = isUserLoggedIn(getState());
        try {
            if (!ssParams.category_id && isLoggedIn) {
                return new Promise((resolve) => {
                    resolve(
                        dispatch({
                            payload: { saveSearchKey: '' },
                            type: SAVE_SEARCH_CHECK_SUCCESS,
                        })
                    );
                });
            }
        } catch (error) {
            const saveSearchKey = createSaveSearchKeyBySearchParams(ssParams);
            dispatch({
                meta: { saveSearchKey },
                type: SAVE_SEARCH_CHECK_FAIL,
            });
        }
    };
